export type Role = 'admin' | 'manager' | 'specialist'

export type WorkStatus =
  | 'new'
  | 'in_progress'
  | 'paused'
  | 'waiting_client'
  | 'done'
  | 'cancelled'

export type PaymentStatus = 'not_payable' | 'to_pay' | 'in_payout' | 'paid'
export type Priority = 'low' | 'normal' | 'high' | 'urgent'
export type PayoutStatus = 'draft' | 'approved' | 'paid'

export interface User {
  id: number
  email: string
  /** Куда слать код входа. Пусто — вход по коду недоступен. */
  contact_email: string | null
  phone: string | null
  full_name: string
  role: Role
  is_active: boolean
  group_id: number | null
  group_name: string | null
  created_at: string
}

/** Телефон или планшет, привязанный к учётной записи коротким кодом. */
export interface Device {
  id: number
  device_uid: string
  label: string | null
  is_active: boolean
  failed_attempts: number
  created_at: string
  last_seen_at: string | null
}

/** Учётка кабинета клиента — так её видит наш менеджер. */
export interface PortalUserRow {
  id: number
  client_id: number
  email: string
  full_name: string
  position: string | null
  phone: string | null
  is_admin: boolean
  is_active: boolean
  created_at: string
  last_login_at: string | null
}

export type TicketStatus =
  | 'new'
  | 'accepted'
  | 'in_progress'
  | 'waiting_client'
  | 'resolved'
  | 'closed'

export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  new: 'Новое',
  accepted: 'Принято',
  in_progress: 'В работе',
  waiting_client: 'Ждём клиента',
  resolved: 'Решено',
  closed: 'Закрыто',
}

export interface TicketMessage {
  id: number
  body: string
  created_at: string
  author_name: string | null
  from_client: boolean
}

export interface TicketNote {
  id: number
  body: string
  created_at: string
  author_name: string | null
}

/** Где стоит согласование работ по заявке. */
export type Approval = 'none' | 'estimated' | 'rework' | 'agreed' | 'rejected'

export interface TicketTaskRef {
  id: number
  title: string
  work_status: WorkStatus
  work_date: string
  /** Чужую задачу откроет только тот, кому позволено видеть все */
  assignee_id: number
  assignee_name: string | null
  /** Сколько наработано по таймеру и сколько оценили */
  hours: number
  estimate: number | null
  is_support: boolean
}

/**
 * Обращение или заявка — делит работу по тому, как за неё платят.
 *
 * Обращение идёт по ежемесячной оплате: сопровождение и аренда 1С покрыты
 * тарифом. Заявка — разовая работа, деньги считаются по факту.
 */
export type TicketKind = 'request' | 'order'

export const TICKET_KIND_LABEL: Record<TicketKind, string> = {
  request: 'Обращение',
  order: 'Заявка',
}

/** Этап заявки. У обращений конвейера нет: там состояние, а не этапы. */
export type OrderStage =
  | 'new'
  | 'cards'
  | 'codes'
  | 'labels'
  | 'in_progress'
  | 'done'
  | 'paid'

export interface StageStep {
  stage: OrderStage
  title: string
  /** Пройден ли шаг и на нём ли заявка сейчас */
  done: boolean
  current: boolean
  at: string | null
}

/** Вид. От него зависят поля формы и кто из наших его берёт. */
export type TicketCategory = 'support' | 'rent_1c' | 'codes' | 'work'

export const TICKET_CATEGORY_LABEL: Record<TicketCategory, string> = {
  support: 'Сопровождение',
  rent_1c: 'Аренда 1С',
  codes: 'Коды',
  work: 'Работы',
}

/** Какие виды относятся к какому роду. Совпадает с KIND_OF на сервере. */
export const CATEGORIES_OF: Record<TicketKind, TicketCategory[]> = {
  request: ['support', 'rent_1c'],
  order: ['codes', 'work'],
}

export interface TicketFile {
  id: number
  original_name: string
  size: number
  uploaded_at: string
  /** Заполненный шаблон ГС1 — по нему считали количество кодов. */
  is_template: boolean
  /** Результат работы: этикетки, акт, выгрузка — то, за чем клиент придёт */
  is_result: boolean
  uploaded_by: string | null
}

/** Кто обратился. Видим только мы — клиент себя и так знает. */
export interface TicketContact {
  company: string | null
  full_name: string | null
  position: string | null
  phone: string | null
  email: string | null
}

export interface Ticket {
  id: number
  client_id: number
  client_name: string | null
  subject: string
  description: string | null
  status: TicketStatus
  category: TicketCategory
  category_title: string
  kind: TicketKind
  kind_title: string
  program: string | null
  direction: string | null
  codes_count: number | null
  /** Конвейер заявки. У обращений пусто. */
  stage: OrderStage | null
  stage_title: string | null
  stage_changed_at: string | null
  due_on: string | null
  overdue: boolean
  /** Сколько часов согласовали с клиентом. Это же видит и он сам. */
  agreed_hours: number | null
  /** Оплата — отдельный факт, а не шаг конвейера */
  paid: boolean
  paid_at: string | null
  paid_by: string | null
  steps: StageStep[]
  author_name: string | null
  assignee_name: string | null
  created_at: string
  updated_at: string
  closed_at: string | null
  messages_count: number
  files_count: number
  /** Клиент написал, а мы ещё не ответили. */
  waiting_for_us: boolean
}

export interface TicketDetail extends Ticket {
  assignee_id: number | null
  // Согласование. Внутреннее: клиент знает только согласованные часы.
  approval: Approval
  approval_title: string
  estimate_hours: number | null
  approval_note: string | null
  approval_at: string | null
  approval_by: string | null
  first_response_at: string | null
  contact: TicketContact
  messages: TicketMessage[]
  files: TicketFile[]
  /** Внутренние заметки. В кабинет клиента не уходят никогда. */
  notes: TicketNote[]
  tasks: TicketTaskRef[]
}

export interface Tariff {
  id: number
  name: string
  description: string | null
  price_per_month: string
  included_hours: string | null
  portal_seats: number | null
  is_active: boolean
  clients_count: number
}

export interface Subscription {
  id: number
  client_id: number
  tariff_id: number
  tariff_name: string | null
  started_on: string
  ends_on: string | null
  price: string
  price_override: string | null
  included_hours: string | null
  note: string | null
  is_current: boolean
  days_left: number | null
}

export interface SubscriptionUsage {
  hours_used: string
  hours_included: string | null
  hours_left: string | null
  period_from: string | null
  period_to: string | null
}

export type Scope = 'own' | 'all'

export interface AccessGroup {
  id: number
  name: string
  description: string | null
  is_admin: boolean
  is_system: boolean
  task_scope: Scope
  client_scope: Scope
  permissions: string[]
  users_count: number
}

export interface PermissionSection {
  section: string
  items: { code: string; label: string }[]
}

/** Права текущего пользователя — по ним интерфейс прячет недоступное. */
export interface MyPermissions {
  group_id: number | null
  group_name: string | null
  is_admin: boolean
  permissions: string[]
  task_scope: Scope
  client_scope: Scope
}

export interface Source {
  id: number
  name: string
  description: string | null
  is_active: boolean
}

export interface Client {
  id: number
  name: string
  inn: string | null
  source_id: number | null
  contact_person: string | null
  phone: string | null
  email: string | null
  note: string | null
  is_active: boolean
  has_projects: boolean
  parent_id: number | null
  /** Кто ведёт клиента с нашей стороны: договор и счета — менеджер, работы — специалист */
  manager_id: number | null
  specialist_id: number | null
  manager_name: string | null
  specialist_name: string | null
  source: Source | null
  parent_name: string | null
  children_count: number
  contacts_count: number
  accesses_count: number
  /** Сколько обычных мест в кабинете. Администратор клиента сверх этого. */
  portal_seats: number
  /** Особые возможности, включённые этому клиенту. У большинства пусто. */
  features: string[]
}

/** Возможность, которую можно включить клиенту. Список приходит с сервера. */
export interface ClientFeature {
  key: string
  title: string
  hint: string
}

export interface NamedRef {
  id: number
  name: string
  is_active: boolean
}

export interface Contact {
  id: number
  client_id: number
  full_name: string
  role: string | null
  phone: string | null
  email: string | null
  telegram: string | null
  note: string | null
  position: number
}

export type AccessKind = 'cloud' | 'database' | 'portal'
export type AccessRowType = 'base_link' | 'computer' | 'credential' | 'portal_entry'

export interface AccessRow {
  id?: number
  row_type: AccessRowType
  position?: number
  url?: string | null
  product_id?: number | null
  product_name?: string | null
  computer?: string | null
  user_name?: string | null
  login?: string | null
  password?: string | null
}

export interface AccessFile {
  id: number
  original_name: string
  size: number
  uploaded_at: string
}

export interface Access {
  id: number
  client_id: number
  kind: AccessKind
  title: string
  login: string | null
  password: string | null
  server_address: string | null
  product_id: number | null
  product_name: string | null
  service_id: number | null
  service_name: string | null
  recovery_phone: string | null
  recovery_email: string | null
  note: string | null
  rows: AccessRow[]
  files: AccessFile[]
  updated_at: string
}

export const ACCESS_KIND_LABEL: Record<AccessKind, string> = {
  cloud: 'Облако',
  database: 'Доступ в базу',
  portal: 'Личный кабинет',
}

export interface Subclient {
  id: number
  client_id: number
  name: string
  address: string | null
  contact_person: string | null
  phone: string | null
  note: string | null
  is_active: boolean
}

export interface Project {
  id: number
  client_id: number
  subclient_id: number | null
  name: string
  description: string | null
  starts_on: string | null
  ends_on: string | null
  is_active: boolean
}

export interface TimeEntry {
  id: number
  task_id: number
  user_id: number
  started_at: string
  stopped_at: string | null
  source: 'timer' | 'manual' | 'imported'
  note: string | null
  seconds: number
}

export interface EstimateLine {
  id: number
  title: string
  hours: string
  position: number
}

/** Строка, которую редактируют в окне оценки (id ещё нет). */
export interface EstimateLineDraft {
  title: string
  hours: string
}

export interface EstimateChange {
  id: number
  user_id: number | null
  user_name: string | null
  old_value: string | null
  new_value: string | null
  comment: string | null
  snapshot: string | null
  changed_at: string
}

export interface Task {
  id: number
  title: string
  description: string | null
  client_id: number
  subclient_id: number | null
  project_id: number | null
  source_id: number | null
  estimate: string | null
  assignee_id: number
  work_status: WorkStatus
  payment_status: PaymentStatus
  priority: Priority
  /** Работа по договору сопровождения: в выплаты не идёт. */
  is_support: boolean
  work_date: string
  due_date: string | null
  rate_override: string | null
  fixed_amount: string | null
  board_order: string | number
  created_at: string
  updated_at: string
  completed_at: string | null
  client_name: string | null
  subclient_name: string | null
  project_name: string | null
  source_name: string | null
  assignee_name: string | null
  /** Из какой заявки или обращения выросла задача. Пусто — завели сами. */
  ticket_id: number | null
  ticket_kind: TicketKind | null
  ticket_title: string | null
  ticket_subject: string | null
  ticket_category_title: string | null
  tracked_seconds: number
  running_since: string | null
  first_started_at: string | null
  last_stopped_at: string | null
  amount: string | number
  rate: string | number
  rate_missing: boolean
  entries: TimeEntry[]
  estimate_lines: EstimateLine[]
  estimate_changes: EstimateChange[]
}

export interface DayPoint {
  day: string
  seconds: number
}

export interface ClientPoint {
  client_id: number
  client_name: string
  seconds: number
  amount: string | number
  tasks: number
}

export interface Kpi {
  tracked_seconds: number
  clients_count: number
  tasks_count: number
  tasks_done: number
  tasks_in_progress: number
  amount_total: string | number
  amount_unpaid: string | number
  amount_paid: string | number
  without_estimate: number
  tasks_without_rate: number
  by_status: Record<string, number>
  by_day: DayPoint[]
  top_clients: ClientPoint[]
}

export interface Rate {
  id: number
  user_id: number
  client_id: number | null
  project_id: number | null
  amount_per_hour: string | number
  valid_from: string
  valid_to: string | null
  note: string | null
  client_name: string | null
  project_name: string | null
  user_name: string | null
}

export interface PayoutItem {
  id: number
  task_id: number
  seconds: number
  hours: string | number
  by_estimate: boolean
  rate: string | number
  amount: string | number
  task_title: string | null
  client_name: string | null
  source_name: string | null
  work_date: string | null
}

export interface Payout {
  id: number
  user_id: number
  user_name: string | null
  period_start: string
  period_end: string
  status: PayoutStatus
  total_seconds: number
  total_amount: string | number
  debt_offset: string | number
  paid_amount: string | number
  note: string | null
  created_at: string
  paid_at: string | null
  items: PayoutItem[]
}

export interface PayoutPreviewRow {
  task_id: number
  work_date: string
  client_name: string
  source_name: string | null
  title: string
  seconds: number
  hours: string | number
  by_estimate: boolean
  rate: string | number
  amount: string | number
  payment_status: PaymentStatus
}

export interface PayoutPreview {
  user_id: number
  user_name: string
  period_start: string
  period_end: string
  rows: PayoutPreviewRow[]
  total_seconds: number
  total_hours: string | number
  total_amount: string | number
  debt_balance: string | number
}

export type DebtKind = 'issued' | 'returned' | 'borrowed' | 'repaid' | 'work_offset' | 'correction'

export interface DebtEntry {
  id: number
  user_id: number
  user_name: string | null
  kind: DebtKind
  amount: string | number
  signed_amount: string | number
  occurred_on: string
  comment: string | null
  payout_id: number | null
  created_by_name: string | null
  created_at: string
}

export interface DebtBalance {
  user_id: number
  user_name: string
  balance: string | number
  issued: string | number
  returned: string | number
  borrowed: string | number
  repaid: string | number
  work_offset: string | number
  entries_count: number
}

export interface DebtKindOption {
  value: DebtKind
  label: string
  sign: number
}

export const DEBT_KIND_LABEL: Record<DebtKind, string> = {
  issued: 'Выдан заём',
  returned: 'Возврат от сотрудника',
  borrowed: 'Заняли у сотрудника',
  repaid: 'Возврат сотруднику',
  work_offset: 'Зачтено работой',
  correction: 'Корректировка',
}

export interface TaskFilters {
  date_from?: string
  date_to?: string
  client_id?: number
  project_id?: number
  source_id?: number
  assignee_id?: number
  without_estimate?: boolean
  /** Незакрытые задачи: работа не закончена или деньги не выплачены. */
  active_only?: boolean
  work_status?: WorkStatus[]
  payment_status?: PaymentStatus[]
  q?: string
}

export const WORK_STATUS_LABEL: Record<WorkStatus, string> = {
  new: 'Новая',
  in_progress: 'В работе',
  paused: 'Пауза',
  waiting_client: 'Ждёт клиента',
  done: 'Завершено',
  cancelled: 'Отменено',
}

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  not_payable: 'Не оплачивается',
  to_pay: 'К выплате',
  in_payout: 'В ведомости',
  paid: 'Выплачено',
}

export const PRIORITY_LABEL: Record<Priority, string> = {
  low: 'Низкий',
  normal: 'Обычный',
  high: 'Высокий',
  urgent: 'Срочно',
}

export const PAYOUT_STATUS_LABEL: Record<PayoutStatus, string> = {
  draft: 'Черновик',
  approved: 'Утверждена',
  paid: 'Выплачена',
}

/** Порядок колонок канбана. */
export const BOARD_COLUMNS: WorkStatus[] = [
  'new',
  'in_progress',
  'paused',
  'waiting_client',
  'done',
]

/** Итог приглашения в кабинет: дошло ли письмо и какую ссылку передать руками. */
export type InviteSent = {
  invited: boolean
  mailed: boolean
  email: string
  link: string | null
}

// --- мессенджер ---

/** Короткая выдержка из другой реплики — для ответа и пересылки. */
export interface QuotedMessage {
  id: number
  author_name: string | null
  body: string
  from_client: boolean
  /** Откуда переслали. У ответа пусто: он всегда в той же беседе. */
  conversation_title: string | null
}

export interface ChatMessage {
  id: number
  body: string
  created_at: string
  author_name: string | null
  /** Реплика клиента или наша */
  from_client: boolean
  file_name: string | null
  file_size: number | null
  reply_to: QuotedMessage | null
  forwarded_from: QuotedMessage | null
  edited_at: string | null
  /** Своя ли реплика — правят только свои */
  editable: boolean
}

export interface Conversation {
  id: number
  client_id: number
  client_name: string | null
  ticket_id: number | null
  ticket_kind: TicketKind | null
  ticket_subject: string | null
  staff_id: number | null
  staff_name: string | null
  title: string
  last_message_at: string | null
  last_from_client: boolean
  preview: string | null
  unread: number
  messages: ChatMessage[]
}

// --- сводка для руководителя ---

export interface SummaryLine {
  title: string
  detail: string
}

export interface SummaryTicket {
  id: number
  kind: TicketKind
  client_name: string | null
  subject: string
  category_title: string
  stage_title: string | null
  assignee_name: string | null
  approval_title: string | null
  estimate_hours: number | null
  due_on: string | null
  days_open: number
}

export interface Summary {
  open_requests: number
  open_orders: number
  waiting_for_us: number
  overdue: number
  unassigned: number
  done_unpaid: number
  waiting_approval: number
  at_specialist: number
  codes_last_30_days: number
  conversations_waiting: number
  orders_by_stage: SummaryLine[]
  requests_by_category: SummaryLine[]
  tariffs_running_out: SummaryLine[]
  oldest_waiting: SummaryTicket[]
  overdue_orders: SummaryTicket[]
  unpaid_orders: SummaryTicket[]
  unassigned_tickets: SummaryTicket[]
  approval_pending: SummaryTicket[]
}

// --- заказ кодов маркировки ---

/** Шаг пути заказа кодов. Виден и нам, и клиенту. */
export type CodeOrderStage =
  | 'new'
  | 'parsed'
  | 'confirmed'
  | 'requested'
  | 'received'
  | 'split'
  | 'done'

export interface CodeOrderStep {
  stage: CodeOrderStage
  title: string
  done: boolean
  current: boolean
}

export type CodeOrderFileKind =
  | 'source'
  | 'cz_request'
  | 'codes'
  | 'pallet_codes'
  | 'pallet_xml'
  | 'gtd'
  | 'report'

export interface CodeOrderFile {
  id: number
  kind: CodeOrderFileKind
  original_name: string
  size: number
  /** Сколько строк внутри — у файлов с кодами */
  rows: number | null
  truck_id: number | null
  item_id: number | null
  pallet_id: number | null
  /** Реквизиты декларации — только у файлов ГТД */
  gtd_number: string | null
  gtd_date: string | null
  uploaded_at: string
  uploaded_by: string | null
}

/** Где паллета на своём пути. Порядок — это и есть путь. */
export type PalletStatus =
  | 'none'
  | 'emitted'
  | 'applied'
  | 'aggregated'
  | 'in_turnover'
  | 'shipped'

export const PALLET_STATUS_LABEL: Record<PalletStatus, string> = {
  none: 'Нет кодов',
  emitted: 'Эмитирован',
  applied: 'Нанесён',
  aggregated: 'Агрегирован',
  in_turnover: 'В обороте',
  shipped: 'Отгружен',
}

/** Порядок шагов — для кнопок «перевести дальше» */
export const PALLET_STATUS_ORDER: PalletStatus[] = [
  'none',
  'emitted',
  'applied',
  'aggregated',
  'in_turnover',
  'shipped',
]

/** Сколько живут эмитированные коды */
export const CODES_LIFETIME_DAYS = 30

export interface CodeOrderPallet {
  id: number
  number: string
  units: number
  /** Номер упаковки. Сейчас это SSCC самой паллеты. */
  sscc: string | null
  status: PalletStatus
  status_title: string
  /** Когда легли коды — от этого дня идёт срок их жизни */
  emitted_at: string | null
  files: CodeOrderFile[]
}

/** Эмитированные коды и сколько им осталось жить */
export interface PalletExpiry {
  pallet_id: number
  number: string
  product: string
  truck: string
  units: number
  emitted_at: string
  /** Отрицательное — уже сгорели */
  days_left: number
}

export interface CodeOrderItem {
  id: number
  product: string
  gtin: string | null
  per_pack: number | null
  packs_per_pallet: number | null
  units_per_pallet: number | null
  pallets_declared: number | null
  units_total: number | null
  pallet_range: string | null
  /** Сколько кодов не хватает. Ноль — всё на месте. */
  codes_short: number
  /** Расхождения, найденные при разборе файла */
  notes: string[]
  pallets: CodeOrderPallet[]
  files: CodeOrderFile[]
}

export interface CodeOrderTruck {
  id: number
  truck_no: string
  order_no: string
  address: string | null
  items: CodeOrderItem[]
  files: CodeOrderFile[]
}

export interface CodeOrder {
  id: number
  client_id: number
  client_name: string | null
  /** Номер поставки у этого клиента — по нему о ней и говорят */
  number: number
  /** Дата поставки, а не дата записи в программе */
  supply_date: string | null
  title: string
  stage: CodeOrderStage
  stage_title: string
  trucks_count: number
  items_count: number
  pallets_count: number
  units_total: number
  author_name: string | null
  /** Завёл клиент из кабинета, а не мы */
  from_client: boolean
  assignee_name: string | null
  created_at: string
  updated_at: string
}

/** Позиция, которую не удалось связать с карточкой товара */
export interface CodeOrderUnmatched {
  item_id: number
  product: string
  truck: string
  /** Сколько строк заказа носят это же название */
  rows: number
}

export interface ClientProductPack {
  gtin: string
  units: number | null
  name: string | null
}

export interface ClientProduct {
  id: number
  gtin: string
  name: string | null
  brand: string | null
  category: string | null
  /** Хватает ли полей, чтобы собрать файл заказа в Честный Знак */
  ready_for_cz: boolean
  packs: ClientProductPack[]
  /** Как этот товар называют в файлах поставки */
  aliases: string[]
}

/** Что получится из собранной руками поставки — до того, как её запишут */
export interface BuildPreview {
  trucks: {
    truck_no: string
    order_no: string
    pallets: number
    units: number
    first: string | null
    last: string | null
  }[]
  trucks_count: number
  items_count: number
  pallets_count: number
  units_total: number
  /** Что не так. Пусто — можно записывать. */
  problem: string | null
}

/** Куда легли присланные файлы кодов */
export interface SpreadResult {
  placed: { item_id: number; product: string; files: number; codes: number }[]
  rejected: { product: string; reason: string }[]
  unknown: { file: string; gtin: string | null }[]
  notes: string[]
}

export interface SplitManyResult {
  done: { product: string; pallets: number }[]
  rejected: { product: string; reason: string }[]
}

export interface ImportResult {
  total: number
  created: number
  message: string
}

export interface CodeOrderDetail extends CodeOrder {
  note: string | null
  org_name: string | null
  org_inn: string | null
  confirmed_at: string | null
  /** Какой номер упаковки программа предложит при выдаче. Правится руками. */
  sscc_next: string | null
  /** Последний выданный номер — от него считается следующий */
  sscc_last: string | null
  /** Сколько паллет в каждом состоянии */
  status_counts: Record<string, number>
  /** Эмитированные коды с их сроком. Просроченные идут первыми. */
  expiring: PalletExpiry[]
  /** Проставлены ли номера упаковок всем паллетам */
  sscc_done: boolean
  steps: CodeOrderStep[]
  trucks: CodeOrderTruck[]
  /** Все расхождения разбора одним списком */
  notes: string[]
  /** Позиции без GTIN. Пока они есть, файл заказа ЧЗ собрать нельзя. */
  unmatched: CodeOrderUnmatched[]
  files: CodeOrderFile[]
}
